import { moveItemInArray, transferArrayItem } from '@nomad2102npm/cdk/drag-drop'

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core'
import { SelectOptionComponent } from '@app/feature/add-field/add-field-content/select-settings/select-option/select-option.component'
import { TbButtonComponent } from '@components-library/tb-button/tb-button.component'
import { TbDragDropEvent } from '@components-library/tb-drag-and-drop/drag-drop.types'
import { TbDragDirective } from '@components-library/tb-drag-and-drop/tb-drag.directive'
import { TbDropContainerDirective } from '@components-library/tb-drag-and-drop/tb-drop-container.directive'
import { TbIconComponent } from '@components-library/tb-icon/tb-icon.component'
import { TbTooltipComponent } from '@components-library/tb-tooltip/tb-tooltip-component/tb-tooltip.component'
import { generateUuid } from '@core/global-util'
import {
    OptionTags,
    SelectObjectOptions,
    SelectOption,
} from '@models/response/select-object-options'
import { Field } from '@models/ui'
import { TranslocoModule } from '@jsverse/transloco'

import { groupBy, map } from 'lodash-es'
import { SelectSettingsFormService } from '@app/feature/add-field/add-field-content/select-settings/select-settings-form.service'
import { Subscription } from 'rxjs'

export interface OptionsByTags {
    active: SelectOption[]
    done: SelectOption[]
    close: SelectOption[]
}

type StatusOptionDragData = {
    optionGuid: string
    tags: keyof OptionsByTags
    index: number
}

@Component({
    selector: 'app-status-select-cell-settings',
    imports: [
        TbIconComponent,
        TbTooltipComponent,
        SelectOptionComponent,
        TbButtonComponent,
        TranslocoModule,
        TbDropContainerDirective,
        TbDragDirective,
    ],
    providers: [SelectSettingsFormService],
    templateUrl: './status-select-cell-settings.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusSelectCellSettingsComponent implements OnInit, OnChanges {
    private readonly selectSettingsFormService = inject(SelectSettingsFormService)

    @Input() field!: Field

    @Input() selectOptions!: SelectObjectOptions

    @Input() template = false

    @Output() fieldChanged: EventEmitter<Field> = new EventEmitter<Field>()
    @Output() valid = new EventEmitter<boolean>()

    optionsByTags!: OptionsByTags
    optionsSorted!: SelectOption[]
    defaultOption!: SelectOption

    optionTags = OptionTags

    statusSubscription?: Subscription
    valueSubscription?: Subscription

    ngOnInit() {
        this.setOptionsAndDefault()

        this.setControls()
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.field) {
            this.setOptionsAndDefault()
            if (!this.selectOptions) return

            this.setControls()
            if (this.field.default_value) return

            this.fieldChanged.emit({
                ...this.field,
                default_value: this.defaultOption.guid,
                select_object_field: this.selectOptions,
            })
        }
    }

    getOptionGroup(guid: string) {
        return this.selectSettingsFormService.getOptionGroup(guid)
    }

    add(tag: keyof OptionsByTags, color = '#cccccc', label = '', addControl = true) {
        const newGuid = `new-${generateUuid()}`
        const newOption: SelectOption = {
            label,
            color,
            tags: tag,
            icon: '',
        }

        if (addControl)
            this.selectSettingsFormService.addGroupControl(newOption, newGuid, this.field)

        this.optionsByTags[tag].push({ guid: newGuid, ...newOption })
        if (this.optionsSorted) {
            this.optionsSorted = [
                ...this.optionsByTags.active,
                ...this.optionsByTags.done,
                ...this.optionsByTags.close,
            ]
        }
        this.selectOptions = {
            ...this.selectOptions,
            [newGuid]: newOption,
        }
        this.isValid(false)
    }

    delete(guid: string, tag: string) {
        this.selectSettingsFormService.deleteGroupControl(guid)
        if (this.optionsSorted) {
            const listOfSpecificTagOptions = this.optionsByTags[tag as keyof OptionsByTags]
            const index = listOfSpecificTagOptions.findIndex((option) => {
                return option.guid === guid
            })
            listOfSpecificTagOptions.splice(index, 1)
            this.sendSortedData()
            return
        }

        delete this.selectOptions[guid]
        this.sendData()
    }

    drop(event: TbDragDropEvent) {
        const sourceData = event.source.data
        const targetData = event.self.data

        if (!this.isStatusOptionDragData(sourceData) || !this.isStatusOptionDragData(targetData))
            return
        if (event.previousIndex === null || event.currentIndex === null) return

        this.moveItemsInGroups(sourceData, targetData, event.currentIndex)
        this.sendSortedData()
    }

    getOptionDragData(
        option: SelectOption,
        tags: keyof OptionsByTags,
        index: number,
    ): StatusOptionDragData {
        return {
            optionGuid: option.guid!,
            tags,
            index,
        }
    }

    trackByOption(index: number, option: SelectOption) {
        return option.guid ?? index
    }

    isValid(valid: boolean) {
        this.valid.emit(valid)
    }

    optionChange(option: SelectOption) {
        const guid = option.guid!
        if (this.optionsSorted) {
            const index = this.optionsSorted.findIndex((option) => {
                return option.guid === guid
            })
            this.optionsSorted.splice(index, 1, option)
            this.fieldChanged.emit({
                ...this.field,
                select_object_field_sorted: this.optionsSorted,
            })
            return
        }

        this.selectOptions[guid] = { ...option }
        this.optionsByTags = this.setOptionsByTags()
        delete this.selectOptions[guid].guid

        this.fieldChanged.emit({
            ...this.field,
            select_object_field: this.selectOptions,
        })
    }

    defaultChange(guid: string) {
        this.field.default_value = guid
        this.setDefault()
        const updatedField = this.optionsSorted
            ? { select_object_field_sorted: this.optionsSorted }
            : { select_object_field: this.selectOptions }

        this.fieldChanged.emit({
            ...this.field,
            ...updatedField,
        })
    }

    private setOptionsAndDefault() {
        if (this.selectOptions) {
            this.optionsByTags = this.setOptionsByTags()
            this.setDefault()
        }

        if (!this.selectOptions) {
            this.preFillForEmptyStatus()
        }
    }

    private setControls() {
        this.statusSubscription?.unsubscribe()
        this.valueSubscription?.unsubscribe()
        this.selectSettingsFormService.setSelectGroupControl(this.selectOptions, this.field)
        this.statusSubscription = this.selectSettingsFormService
            .getStatusChanges()
            .subscribe((status) => {
                this.isValid(status === 'VALID')
            })

        this.valueSubscription = this.selectSettingsFormService
            .getValueChanges()
            .subscribe((value) => {
                value.forEach((option) => {
                    this.optionChange({
                        ...option,
                        tags: this.selectOptions[option.guid!]?.tags,
                    })
                })
            })
    }

    private moveItemsInGroups(
        sourceData: StatusOptionDragData,
        targetTag: StatusOptionDragData,
        currentIndex: number,
    ) {
        const sourceList = this.optionsByTags[sourceData.tags]
        const targetList = this.optionsByTags[targetTag.tags]

        if (sourceData.tags === targetTag.tags) {
            moveItemInArray(targetList, sourceData.index, currentIndex)
            return
        }

        if (sourceList.length === 1) return

        transferArrayItem(sourceList, targetList, sourceData.index, currentIndex)
        targetList[currentIndex].tags = targetTag.tags
    }

    private sendSortedData() {
        this.optionsSorted = [
            ...this.optionsByTags.active,
            ...this.optionsByTags.done,
            ...this.optionsByTags.close,
        ]

        this.fieldChanged.emit({
            ...this.field,
            select_object_field_sorted: this.optionsSorted,
        })
    }

    private sendData() {
        this.optionsByTags = this.setOptionsByTags()
        this.fieldChanged.emit({
            ...this.field,
            select_object_field: this.selectOptions,
        })
    }

    private setDefault() {
        const defaultGuid = this.getDefault()
        this.defaultOption = {
            guid: defaultGuid,
            ...this.selectOptions[defaultGuid],
        }
    }

    private getDefault() {
        const defaultValue = this.field.default_value
        if (!defaultValue) {
            return this.optionsByTags?.active[0].guid ?? ''
        }

        return defaultValue
    }

    private setOptionsByTags(): OptionsByTags {
        const selectOptions = map(this.selectOptions, (value, guid) => ({ guid, ...value }))
        return groupBy(selectOptions, 'tags') as Record<'active' | 'done' | 'close', SelectOption[]>
    }

    private preFillForEmptyStatus() {
        // TODO: remove or update after status default options on workspace creation will be implemented
        this.optionsByTags = {
            active: [],
            done: [],
            close: [],
        }

        this.add('active', '#C5E1A5', 'New', false)
        this.add('done', '#FFE0B2', 'Under review', false)
        this.add('close', '#FF9E80', 'Closed', false)
        this.setDefault()
        this.fieldChanged.emit({
            ...this.field,
            default_value: this.defaultOption.guid,
            select_object_field: this.selectOptions,
        })
        this.isValid(true)
    }

    private isStatusOptionDragData(data: Record<string, unknown>): data is StatusOptionDragData {
        return !!data.optionGuid && typeof data.index === 'number' && !!data.tags
    }
}
